import { Bus } from '../game/audio/AudioEngine';

/**
 * Trzy suwaki: muzyka, tło, efekty.
 *
 * A component rather than a panel, because it has no place of its own: it is
 * shown inside the game menu, under Ustawienia. The first version was a button
 * in the corner of the screen, which put a control nobody touches twice into
 * the same space as the ones they touch constantly.
 *
 * **Three sliders and not one.** Music, ambience and footsteps are turned down
 * for different reasons — somebody plays their own music, somebody finds the sea
 * loud, somebody is on a call. A single volume answers none of those without
 * also answering the others.
 *
 * **The setting is remembered, and that is the point of having it.** Turning the
 * music down every time the page reloads is the same as not being able to turn
 * it down. `localStorage` is per browser, which is the right scope: this is a
 * fact about where somebody is sitting, not about their account.
 */

const STORAGE = 'winged.sound';

/** Where a bus starts before anybody has said otherwise. */
const DEFAULTS: Record<Bus, number> = { music: 0.5, ambience: 0.7, effects: 0.8 };

const LABELS: Record<Bus, string> = {
  music: 'Muzyka',
  ambience: 'Tło',
  effects: 'Efekty',
};

const ORDER: Bus[] = ['music', 'ambience', 'effects'];

const clamp = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * What was set last time, or the defaults.
 *
 * Storage that throws — a private window, a browser told to keep nothing — is
 * the same answer as storage that is empty. Volume is not worth an exception on
 * the way into the game.
 */
export function storedVolumes(): Record<Bus, number> {
  try {
    const raw = localStorage.getItem(STORAGE);
    if (!raw) return { ...DEFAULTS };
    const saved = JSON.parse(raw) as Partial<Record<Bus, number>>;
    return {
      music: clamp(saved.music ?? DEFAULTS.music),
      ambience: clamp(saved.ambience ?? DEFAULTS.ambience),
      effects: clamp(saved.effects ?? DEFAULTS.effects),
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(volumes: Record<Bus, number>): void {
  try {
    localStorage.setItem(STORAGE, JSON.stringify(volumes));
  } catch {
    // Nothing to do about a browser that keeps nothing, and nothing worth
    // saying: the sliders still work for this sitting.
  }
}

/**
 * The three of them, as one block to put somewhere.
 *
 * Told on every drag rather than on release: a volume that only changes when
 * the mouse is let go cannot be set by ear, which is the only way anybody sets
 * one.
 */
export function volumeSliders(onChange: (bus: Bus, value: number) => void): HTMLElement {
  const volumes = storedVolumes();

  const root = document.createElement('div');
  root.className = 'sound-sliders';

  for (const bus of ORDER) {
    const row = document.createElement('label');
    row.className = 'sound-row';

    const name = document.createElement('span');
    name.className = 'sound-name';
    name.textContent = LABELS[bus];

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.step = '1';
    slider.value = String(Math.round(volumes[bus] * 100));
    slider.className = 'sound-slider';

    const readout = document.createElement('span');
    readout.className = 'sound-value';
    readout.textContent = slider.value;

    slider.addEventListener('input', () => {
      const value = clamp(Number(slider.value) / 100);
      volumes[bus] = value;
      readout.textContent = String(Math.round(value * 100));
      onChange(bus, value);
      save(volumes);
    });

    row.append(name, slider, readout);
    root.appendChild(row);
  }

  return root;
}
